/**
 * Layout primitives for the global bar, kept dependency-free.
 *
 * `AppTopBar` and the Sentry error boundary both render the bar, but the
 * boundary must stay renderable after the router has thrown, so it cannot import
 * the component. Both import these strings instead, which keeps the two in sync
 * without coupling the boundary to router/sidebar modules.
 *
 * ## Drag contract
 *
 * The bar is the window's title bar: the shell is frameless on Windows and
 * macOS, so this element is the only thing a user can grab to move the window.
 * That makes the rule one-directional — **the bar and every zone inside it are
 * drag surfaces, and only controls opt out**. `-webkit-app-region` is not
 * inherited and a child that leaves it unset does not subtract from its
 * ancestor's drag rect, so a zone stays draggable simply by not saying
 * otherwise. `index.css` already exempts `button`, `a`, `input`, `textarea` and
 * `select` inside `.drag-region`, which covers nearly everything the bar
 * renders; `no-drag-region` is for the rest — a control built on a `div` or
 * `span` (a Base UI trigger with `render`, a `tabIndex` hover target).
 *
 * Never put `no-drag-region` on a container that stretches to fill the bar's
 * slack. Both zones and the breadcrumb's `nav` used to carry it, and between
 * them they claimed every free pixel: the login bar was a wide empty strip that
 * could not move the window at all.
 */

import { cn } from "~/lib/utils";

/**
 * Two zones, not three.
 *
 * The bar used to centre a wordmark between equal side columns. The wordmark
 * now lives in the sidebar header, and what replaced it — the breadcrumb — is
 * left-anchored identity, not a centrepiece. So the left zone takes the slack
 * and the right zone sizes to its actions. Both are `minmax(0, …)` so a long
 * breadcrumb truncates instead of pushing the actions off the bar.
 */
export const APP_TOP_BAR_GRID_CLASS = "grid grid-cols-[minmax(0,1fr)_minmax(0,auto)] items-center";

/** Shared chrome for the bar element itself (height + safe-area live in CSS). */
/**
 * The bar also opens the `header-actions` container.
 *
 * The action controls (`ProjectScriptsControl`, `GitActionsControlView`,
 * `OpenInPicker`) reveal their text labels at `@3xl/header-actions`. Those rules
 * shipped with nothing declaring the container, so they never matched and the
 * labels were pinned `sr-only` — the buttons rendered as bare icons forever.
 *
 * It is declared *here* and not on the action strip itself for the reason the
 * strip documents: `container-type: inline-size` stops an element's contents
 * contributing to its intrinsic width, and the bar sizes its action column from
 * exactly that, so declaring it there collapsed the column to 4px. This element
 * is `w-full` — its width comes from the window — so it can carry the container
 * without any layout depending on its contents.
 */
export const APP_TOP_BAR_CLASS = cn(
  "forge-app-top-bar drag-region @container/header-actions relative z-20 w-full min-w-0 shrink-0 bg-background",
  APP_TOP_BAR_GRID_CLASS,
);

/**
 * Left zone: the collapse trigger (only while the sidebar is hidden) followed by
 * the breadcrumb. `min-w-0` is load-bearing — without it the breadcrumb's
 * truncation never engages and a long thread title overflows the bar.
 *
 * No `no-drag-region` here, deliberately. This zone sits on the `1fr` track, so
 * it *is* the bar's empty space; opting it out of dragging is what left the
 * window immovable by its own title bar. See the drag contract at the top of
 * this file.
 */
export const APP_TOP_BAR_LEFT_ZONE_CLASS = "flex h-full min-w-0 items-center gap-1 md:gap-1.5";

/**
 * Wordmark button, used only by the crash boundary.
 *
 * In the running app the wordmark belongs to the sidebar header. The boundary
 * has no sidebar — it replaces the whole shell — so it is the one surface where
 * the bar still carries brand identity.
 */
export const APP_TOP_BAR_LOGO_BUTTON_CLASS =
  "flex h-touch min-w-0 max-w-full touch-manipulation items-center justify-center rounded-lg px-3 text-foreground transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring md:h-7 md:px-2";

/**
 * Keeps the bar clear of the right panel's column.
 *
 * An inline style rather than a utility: the value is a `var()` with a fallback,
 * and Tailwind emitted no rule for the arbitrary class — the class landed in the
 * DOM and computed to `0px`, which looks exactly like a working style until you
 * read the computed value.
 *
 * Without it the bar spans the full window and paints across the panel's own tab
 * strip (bar `z-20`, panel `z-10`), which is what made the panel read as a
 * drawer under the breadcrumb rather than the third column. The panel publishes
 * the width on the document root; the `0px` fallback means nothing moves while
 * it is closed.
 */
export const APP_TOP_BAR_STYLE = {
  // `width`, not `padding-inline-end`. Padding reserves the space but leaves
  // the element full-width, so anything it paints — background, and previously
  // a bottom border — still ran straight across the panel's own strip.
  // Narrowing the element ends the bar where the panel's column begins, which
  // is what makes the two read as columns rather than a bar over a drawer.
  width: "calc(100% - var(--forge-right-panel-reserved-width, 0px))",
} as const;
